import { useCallback, useMemo, useState } from "react"

import useAttachmentActions from "@hooks/useAttachmentActions"
import useEditingLock from "@hooks/useEditingLock"

import { faIcon } from "@utils/faIcon"
import { formatFileSize } from "@utils/fileUtils"
import { formatStandardDateString } from "@utils/formatDate"

import { CollaborationRoom } from "@particles"

import { Button } from "@atoms"

import UserAvatar from "@molecules/UserAvatar/UserAvatar"
import { EditingUserAvatar, Prompt, ValueDisplay } from "@molecules/index"

import SidePanel from "@organisms/SidePanel/SidePanel"
import SidePanelFooter from "@organisms/SidePanel/SidePanelFooter/SidePanelFooter"
import SidePanelHeader from "@organisms/SidePanel/SidePanelHeader/SidePanelHeader"
import { Menu } from "@organisms/index"

import styles from "./FileSidePanel.module.scss"
import type { FileSidePanelContentProps, FileSidePanelProps } from "./FileSidePanel.types"
import RenameFileDialog from "./RenameFileDialog"

function FileSidePanelContent(props: FileSidePanelContentProps) {
    const {
        attachments,
        endpointBase,
        initialIndex = 0,
        isPublic = false,

        isOpen,
        onOpenChange,
        onAttachmentDeleted,
        onAttachmentUpdated,

        currentIndex,
        onCurrentIndexChange,
    } = props

    const isEditable = !isPublic

    const [isDeletePromptOpen, setIsDeletePromptOpen] = useState(false)
    const [isVisibilityPromptOpen, setIsVisibilityPromptOpen] = useState(false)
    const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false)

    const { deleteAttachment, toggleVisibility, isDeleting, isTogglingVisibility } = useAttachmentActions(endpointBase)

    const currentAttachment = attachments[currentIndex]

    const formId = `attachment-rename-${currentAttachment?.id}`
    const { isLocked, lockingUser, startEditing, stopEditing } = useEditingLock(formId)

    const handleFileDownload = useCallback(() => {
        if (!currentAttachment?.signed_url) {
            return
        }

        void (async () => {
            const response = await fetch(currentAttachment.signed_url)
            const blobData = await response.blob()
            const objectUrl = URL.createObjectURL(blobData)

            const anchor = document.createElement("a")
            anchor.download = currentAttachment.upload_filename
            anchor.href = objectUrl
            document.body.appendChild(anchor)
            anchor.click()
            anchor.remove()

            URL.revokeObjectURL(objectUrl)
        })()
    }, [currentAttachment])

    const handleDelete = useCallback(() => {
        if (!currentAttachment) {
            return
        }

        void (async () => {
            const success = await deleteAttachment(currentAttachment.id)
            if (success) {
                const wasLastFile = attachments.length === 1

                if (wasLastFile) {
                    onOpenChange?.(false)
                } else if (currentIndex >= attachments.length - 1) {
                    onCurrentIndexChange(currentIndex - 1)
                }

                onAttachmentDeleted?.(currentAttachment.id)
                setIsDeletePromptOpen(false)
            }
        })()
    }, [
        currentAttachment,
        attachments.length,
        currentIndex,
        onCurrentIndexChange,
        onAttachmentDeleted,
        onOpenChange,
        deleteAttachment,
    ])

    const handleToggleVisibility = useCallback(() => {
        if (!currentAttachment) {
            return
        }

        void (async () => {
            const result = await toggleVisibility(currentAttachment.id, currentAttachment.is_public)
            if (result) {
                onAttachmentUpdated?.(currentAttachment.id, { is_public: result.is_public })
                setIsVisibilityPromptOpen(false)
            }
        })()
    }, [currentAttachment, onAttachmentUpdated, toggleVisibility])

    const handleOpenRenameDialog = useCallback(() => {
        startEditing()
        setIsRenameDialogOpen(true)
    }, [startEditing])

    const handleCloseRenameDialog = useCallback(
        (open: boolean) => {
            if (!open) {
                stopEditing()
                setIsRenameDialogOpen(false)
            }
        },
        [stopEditing],
    )

    const handleRenameSuccess = useCallback(
        (attachmentId: string, newFilename: string) => {
            stopEditing()
            setIsRenameDialogOpen(false)
            onAttachmentUpdated?.(attachmentId, { upload_filename: newFilename })
        },
        [stopEditing, onAttachmentUpdated],
    )

    const menuItems = useMemo(() => {
        if (!currentAttachment) {
            return []
        }

        const isPublic = currentAttachment.is_public
        return [
            {
                type: "group" as const,
                items: [
                    {
                        label: isPublic ? "Make private" : "Make public",
                        icon: faIcon({ name: isPublic ? "eye-slash" : "eye", style: "regular" }),
                        onClick: () => setIsVisibilityPromptOpen(true),
                    },
                    {
                        label: "Rename",
                        icon: faIcon({ name: "i-cursor", style: "regular" }),
                        onClick: handleOpenRenameDialog,
                    },
                ],
            },
            { type: "divider" as const },
            {
                type: "group" as const,
                items: [
                    {
                        label: "Delete",
                        variant: "danger" as const,
                        icon: faIcon({ name: "trash-can", style: "regular" }),
                        onClick: () => setIsDeletePromptOpen(true),
                    },
                ],
            },
        ]
    }, [currentAttachment, handleOpenRenameDialog])

    if (!currentAttachment) {
        return null
    }

    const accessLabel = currentAttachment.is_public ? "Public" : "Private"
    const uploadedBy = currentAttachment.uploaded_by
    const visibilityPromptTitle = currentAttachment.is_public ? "Make file private?" : "Make file public?"
    const visibilityPromptBody = currentAttachment.is_public
        ? "This will restrict access to only your internal team members. Your client will no longer be able to view this file."
        : "This will grant read-only access to your client."

    const renderActionsButton = () => {
        if (isLocked && lockingUser) {
            return <EditingUserAvatar user={lockingUser} size="md" />
        }

        return (
            <Menu
                trigger={
                    <Button size="lg" variant="ghost" leftIcon={faIcon({ name: "bars", style: "regular" })}>
                        Actions
                    </Button>
                }
                items={menuItems}
                popoverSide="top"
                popoverAlign="end"
            />
        )
    }

    return (
        <>
            <SidePanel.Root isOpen={isOpen} onOpenChange={onOpenChange}>
                <SidePanel.Content>
                    <SidePanel.FilePreview
                        files={attachments}
                        initialIndex={initialIndex}
                        isNavigationDisabled={isDeletePromptOpen || isVisibilityPromptOpen || isRenameDialogOpen}
                        onIndexChange={onCurrentIndexChange}
                    />

                    <SidePanel.Panel>
                        <SidePanelHeader.Root>
                            <SidePanelHeader.Title title={currentAttachment.upload_filename} />
                            <SidePanelHeader.CloseButton />
                        </SidePanelHeader.Root>

                        <SidePanel.Body>
                            <div className={styles.metadataContainer}>
                                <div className={styles.metadataRow}>
                                    <ValueDisplay variant="horizontal" label="Access" value={accessLabel} />
                                </div>

                                <div className={styles.metadataRow}>
                                    <ValueDisplay
                                        variant="horizontal"
                                        label="Uploaded on"
                                        value={formatStandardDateString(currentAttachment.uploaded_at)}
                                    />
                                </div>

                                {uploadedBy && (
                                    <div className={styles.metadataRow}>
                                        <ValueDisplay
                                            variant="horizontal"
                                            label="Uploaded by"
                                            showIcon={false}
                                            value={
                                                <span className={styles.uploadedByValue}>
                                                    <UserAvatar user={uploadedBy} size="sm" />
                                                    {uploadedBy.full_name}
                                                </span>
                                            }
                                        />
                                    </div>
                                )}

                                <div className={styles.metadataRow}>
                                    <ValueDisplay
                                        variant="horizontal"
                                        label="Size"
                                        value={formatFileSize(currentAttachment.upload_size)}
                                    />
                                </div>
                            </div>
                        </SidePanel.Body>

                        <SidePanelFooter.Root>
                            <SidePanelFooter.RightSide>
                                {currentAttachment.signed_url && (
                                    <Button
                                        size="lg"
                                        variant="subtle"
                                        isFullWidth={true}
                                        onClick={handleFileDownload}
                                        leftIcon={faIcon({ name: "download", style: "regular" })}
                                        style={{ flex: 1 }}
                                    >
                                        Download
                                    </Button>
                                )}
                                {isEditable && renderActionsButton()}
                            </SidePanelFooter.RightSide>
                        </SidePanelFooter.Root>
                    </SidePanel.Panel>
                </SidePanel.Content>
            </SidePanel.Root>

            {isEditable && (
                <RenameFileDialog
                    attachment={currentAttachment}
                    endpointBase={endpointBase}
                    isOpen={isRenameDialogOpen}
                    onOpenChange={handleCloseRenameDialog}
                    onSuccess={handleRenameSuccess}
                />
            )}

            {isEditable && (
                <>
                    <Prompt
                        isOpen={isVisibilityPromptOpen}
                        onOpenChange={setIsVisibilityPromptOpen}
                        title={visibilityPromptTitle}
                        body={visibilityPromptBody}
                        confirmText={currentAttachment.is_public ? "Make private" : "Make public"}
                        cancelText="Cancel"
                        onConfirm={handleToggleVisibility}
                        isLoading={isTogglingVisibility}
                    />

                    <Prompt
                        isOpen={isDeletePromptOpen}
                        onOpenChange={setIsDeletePromptOpen}
                        title="Delete file?"
                        body="This action cannot be undone. The file will be permanently deleted."
                        confirmText="Delete"
                        cancelText="Cancel"
                        onConfirm={handleDelete}
                        isDangerousOperation={true}
                        isLoading={isDeleting}
                    />
                </>
            )}
        </>
    )
}

export default function FileSidePanel(props: FileSidePanelProps) {
    const { attachments, initialIndex = 0 } = props

    const [currentIndex, setCurrentIndex] = useState(initialIndex)

    const currentAttachment = attachments[currentIndex]
    const roomName = currentAttachment?.id != null ? `attachment-rename-${currentAttachment.id}` : undefined

    return (
        <CollaborationRoom roomName={roomName}>
            <FileSidePanelContent {...props} currentIndex={currentIndex} onCurrentIndexChange={setCurrentIndex} />
        </CollaborationRoom>
    )
}
